import { useCallback, useEffect, useRef, useState } from 'react';
import { chatbotApi, ChatbotApiError } from './chatbotApi';
import { ChatbotUiMessage } from './types';

const SESSION_KEY = 'chatbot.session_id';

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function messageForError(e: unknown): string {
  if (e instanceof ChatbotApiError) {
    switch (e.code) {
      case 'rate_limited': {
        const retry = e.details?.retry_after_seconds;
        return retry
          ? `Slow down — try again in ${retry}s.`
          : 'Slow down — try again in a few seconds.';
      }
      case 'llm_unavailable':
        return 'The assistant is offline. Please try again in a minute.';
      case 'cypher_execution_failed':
        return "Couldn't query the graph. Try rephrasing.";
      case 'unsafe_cypher':
        return e.message || "That query couldn't be run safely.";
      default:
        if (e.status === 422) return "Message can't be empty.";
        return e.message || 'Something went wrong.';
    }
  }
  if (e instanceof DOMException && e.name === 'AbortError') {
    return 'Request cancelled.';
  }
  return 'Network error.';
}

export interface UseChatbotSessionOptions {
  /**
   * Fired after a turn is sent while authenticated (i.e. the backend persisted
   * it). Use it to refresh the history list — the first turn of a new
   * conversation creates and auto-titles its row server-side.
   */
  onTurnPersisted?: (sessionId: string) => void;
}

export interface UseChatbotSession {
  messages: ChatbotUiMessage[];
  sending: boolean;
  loading: boolean;
  sessionId: string | null;
  send: (message: string, selectedOption?: string) => Promise<void>;
  /** Start a fresh conversation locally — no backend call (per history spec). */
  newConversation: () => void;
  /**
   * Reopen a stored conversation by id, loading its transcript. Pass
   * `{ quiet: true }` for first-mount hydration so a missing/unreachable
   * session leaves an empty composer instead of an error card.
   */
  openConversation: (sessionId: string, opts?: { quiet?: boolean }) => Promise<void>;
  /** Legacy in-session reset — clears state and calls /session/reset. */
  reset: () => Promise<void>;
  abort: () => void;
}

export function useChatbotSession(
  options: UseChatbotSessionOptions = {}
): UseChatbotSession {
  const { onTurnPersisted } = options;
  const [messages, setMessages] = useState<ChatbotUiMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(
    typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_KEY) : null
  );
  const abortRef = useRef<AbortController | null>(null);

  // Keep a ref in sync so `send`/`openConversation` always read the latest id
  // without re-creating the callbacks.
  const sessionIdRef = useRef<string | null>(sessionId);
  const onPersistedRef = useRef(onTurnPersisted);
  useEffect(() => {
    onPersistedRef.current = onTurnPersisted;
  }, [onTurnPersisted]);

  const setCurrentSession = useCallback((id: string | null) => {
    sessionIdRef.current = id;
    setSessionId(id);
    if (typeof window !== 'undefined') {
      if (id) sessionStorage.setItem(SESSION_KEY, id);
      else sessionStorage.removeItem(SESSION_KEY);
    }
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const send = useCallback(
    async (message: string, selectedOption?: string) => {
      const trimmed = message.trim();
      if (!trimmed) return;

      // Mint the conversation id client-side on the first turn so we can track
      // it (and highlight it in the history sidebar) before the server replies.
      let activeId = sessionIdRef.current;
      if (!activeId) {
        activeId = newId();
        setCurrentSession(activeId);
      }

      const userMsg: ChatbotUiMessage = {
        kind: 'user',
        id: newId(),
        text: trimmed,
      };
      const pendingId = newId();
      setMessages((m) => [...m, userMsg, { kind: 'pending', id: pendingId }]);
      setSending(true);

      // Abort any in-flight request — §11.8 race-condition guidance.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const resp = await chatbotApi.postChat(
          {
            session_id: activeId,
            message: trimmed,
            selected_option: selectedOption,
          },
          controller.signal
        );

        // The server echoes the canonical session id — adopt it in case it
        // differs (e.g. server-side migration of an expired id).
        setCurrentSession(resp.session_id);
        setMessages((m) =>
          m.map((x) =>
            x.id === pendingId ? { kind: 'assistant', id: pendingId, data: resp } : x
          )
        );

        // Every turn is persisted now (JWT disabled → shared "public"
        // bucket), so always refresh the history list.
        onPersistedRef.current?.(resp.session_id);
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          // Drop the pending placeholder silently — the next turn already replaced it.
          setMessages((m) => m.filter((x) => x.id !== pendingId));
          return;
        }
        const text = messageForError(e);
        setMessages((m) =>
          m.map((x) =>
            x.id === pendingId
              ? {
                  kind: 'error',
                  id: pendingId,
                  text,
                  retry: () => {
                    // Remove the failed placeholder before retrying so the
                    // user doesn't see two attempts stacked.
                    setMessages((curr) => curr.filter((c) => c.id !== pendingId));
                    void send(trimmed, selectedOption);
                  },
                }
              : x
          )
        );
      } finally {
        if (abortRef.current === controller) {
          setSending(false);
          abortRef.current = null;
        }
      }
    },
    [setCurrentSession]
  );

  const newConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSending(false);
    setLoading(false);
    setMessages([]);
    // Clear the id so the next message mints a fresh one — no backend call.
    setCurrentSession(null);
  }, [setCurrentSession]);

  const openConversation = useCallback(
    async (id: string, opts?: { quiet?: boolean }) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setMessages([]);
      try {
        const detail = await chatbotApi.getConversation(id, controller.signal);
        const ui = detail.messages.map((m, i) =>
          chatbotApi.storedMessageToUi(m, detail.sessionId || id, i)
        );
        setMessages(ui);
        setCurrentSession(detail.sessionId || id);
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        // Quiet hydration (first mount): a missing/expired or briefly
        // unreachable session shouldn't replace the thread with an error card —
        // leave an empty composer; the row stays in the sidebar to retry.
        if (opts?.quiet) {
          setMessages([]);
          return;
        }
        const text = messageForError(e);
        setMessages([
          {
            kind: 'error',
            id: newId(),
            text: text || "Couldn't load that conversation.",
            retry: () => void openConversation(id),
          },
        ]);
        setCurrentSession(id);
      } finally {
        if (abortRef.current === controller) {
          setLoading(false);
          abortRef.current = null;
        }
      }
    },
    [setCurrentSession]
  );

  // First mount: the active conversation's messages live server-side, not in
  // component state, so a page reload or navigate-back must reload the
  // transcript instead of showing an empty thread. Hydrate from the persisted
  // session id (restored from sessionStorage above). (#1 chat-switch fix)
  useEffect(() => {
    const restored = sessionIdRef.current;
    if (restored) {
      void openConversation(restored, { quiet: true });
    }
    // openConversation is stable; intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(async () => {
    abortRef.current?.abort();
    const stored = sessionIdRef.current;
    if (stored) {
      try {
        await chatbotApi.resetSession(stored);
      } catch {
        // Server-side reset is best-effort — clear local state regardless.
      }
    }
    setCurrentSession(null);
    setMessages([]);
    // Clearing a session is a mutation too — refresh the sidebar list (#2).
    onPersistedRef.current?.(stored ?? '');
  }, [setCurrentSession]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSending(false);
  }, []);

  return {
    messages,
    sending,
    loading,
    sessionId,
    send,
    newConversation,
    openConversation,
    reset,
    abort,
  };
}
